import React, { useEffect, useState, useCallback } from "react";
import { Gift, Loader2, Save, Check, Ticket } from "lucide-react";
import { api, adminHeaders } from "@/lib/api";
import { toast } from "sonner";

const h = () => ({ headers: adminHeaders() });
const STATUS = ["issued", "applied", "fulfilled", "void"];
const STATUS_CLS = {
  issued: "border-gold/40 text-gold-soft", applied: "border-emerald-500/40 text-emerald-300",
  fulfilled: "border-emerald-500/40 text-emerald-300", void: "border-red-500/40 text-red-300",
};

export default function AdminRewards() {
  const [settings, setSettings] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [reds, setReds] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/membership-settings", h()).then(({ data }) => { setSettings(data); setCatalog(data.reward_catalog || []); }).catch(() => {});
    api.get("/admin/rewards/redemptions", h()).then(({ data }) => setReds(data.redemptions || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setItem = (i, k, v) => setCatalog((c) => { const a = [...c]; a[i] = { ...a[i], [k]: v }; return a; });

  const saveCatalog = async () => {
    setSaving(true);
    try {
      const clean = catalog.map((r) => ({ ...r, points_cost: Number(r.points_cost) || 0, value: Number(r.value) || 0 }));
      await api.put("/admin/membership-settings", { reward_catalog: clean }, h());
      toast.success("Reward catalog saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const setStatus = async (rid, status) => {
    try {
      await api.post(`/admin/rewards/redemptions/${rid}/status`, { status }, h());
      setReds((rs) => rs.map((r) => r.id === rid ? { ...r, status } : r));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  if (!settings) return <div className="py-16 flex justify-center text-muted-c"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-rewards">
      <div>
        <p className="kicker mb-1">Loyalty</p>
        <h2 className="font-display text-2xl font-semibold text-white flex items-center gap-2"><Gift className="h-5 w-5 text-gold-soft" /> Rewards</h2>
      </div>

      {/* Catalog editor */}
      <div className="ascendia-card p-6" data-testid="admin-reward-catalog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-white">Reward catalogue</h3>
          <button onClick={saveCatalog} disabled={saving} data-testid="save-catalog" className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
        <div className="space-y-2">
          {catalog.map((r, i) => (
            <div key={r.id} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-[color:var(--border-subtle)] p-2.5" data-testid={`catalog-row-${r.id}`}>
              <span className="col-span-5 text-sm text-white truncate">{r.title}</span>
              <span className="col-span-2 text-xs text-muted-c uppercase">{r.type}</span>
              <label className="col-span-2 flex items-center gap-1 text-xs text-muted-c">₪<input value={r.value ?? 0} onChange={(e) => setItem(i, "value", e.target.value)} className="w-full rounded bg-black/40 border border-[color:var(--border-subtle)] px-2 py-1 text-white" /></label>
              <label className="col-span-2 flex items-center gap-1 text-xs text-muted-c"><input value={r.points_cost} onChange={(e) => setItem(i, "points_cost", e.target.value)} data-testid={`catalog-cost-${r.id}`} className="w-full rounded bg-black/40 border border-[color:var(--border-subtle)] px-2 py-1 text-gold-soft" />pts</label>
              <button onClick={() => setItem(i, "active", !(r.active !== false))} className={`col-span-1 rounded px-2 py-1 text-[10px] border ${r.active !== false ? "border-emerald-500/40 text-emerald-300" : "border-white/20 text-muted-c"}`}>{r.active !== false ? "On" : "Off"}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Redemptions */}
      <div className="ascendia-card p-6" data-testid="admin-redemptions">
        <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2"><Ticket className="h-4 w-4 text-gold-soft" /> Claims ({reds.length})</h3>
        {reds.length === 0 ? <p className="text-sm text-muted-c">No reward claims yet.</p> : (
          <div className="space-y-2">
            {reds.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--border-subtle)] p-3" data-testid={`redemption-${r.id}`}>
                <div>
                  <p className="text-sm text-white">{r.title} <span className="font-mono text-gold-soft text-xs">{r.code}</span></p>
                  <p className="text-xs text-muted-c">{r.client_name} · {r.points_cost} pts · {(r.created_at || "").slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] uppercase rounded-full border px-2 py-0.5 ${STATUS_CLS[r.status] || ""}`}>{r.status}</span>
                  {STATUS.filter((s) => s !== r.status).map((s) => (
                    <button key={s} onClick={() => setStatus(r.id, s)} className="text-[10px] rounded border border-[color:var(--border-subtle)] px-2 py-0.5 text-white/70 hover:bg-white/5">{s}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
